import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { TranslationTag } from '@ps/web-core-intl';
import { select } from '@ps/web-core-store';
import { emailTagTransform } from '@ps/web-core-utils';
import { StarsLink } from '@ps/web-shared-ui';
import { useMemo } from 'react';
import type { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

export interface Options extends Pick<TranslationTag, 'resolve'> {
  domain?: string;
  values?: React.ComponentProps<typeof FormattedMessage>['values'];
}

const POKER_STARS_CASINO = 'PokerStars Casino';
const POKER_STARS_VEGAS = 'PokerStars Vegas';

export const useTranslationValues = ({ resolve, domain, values: rawValues }: Options = {}) => {
  const appDomain = useSelector(select.cms.getDomain);
  const brand = useSelector(select.cms.getBrandId);
  const brandName = useSelector(select.cms.getBrandName);
  const starsDomain = useSelector(select.cms.getStarsDomain);
  const isSiteDE = useSelector(select.cms.isSiteDE);
  const rewardsBrand = useSelector(select.rewards.getRewardsBrand);

  const values = useMemo(() => {
    let computedBrand;

    if (isSiteDE && [BrandTypes.STARS_CASINO, BrandTypes.POKER_STARS_VEGAS].includes(brand)) {
      computedBrand = POKER_STARS_VEGAS;
    } else if (brand === BrandTypes.STARS_CASINO) {
      computedBrand = POKER_STARS_CASINO;
    } else {
      computedBrand = brandName;
    }

    const newValues: Record<string, string | React.ReactElement> = {
      appDomain,
      appStarsdomain: starsDomain,
      accountBrand: brand === BrandTypes.FOX_BET ? 'User' : 'Stars',
      brandCoin: 'StarsCoin',
      brand: computedBrand,
      rewardsBrand,
      ...rawValues
    };

    if (resolve) {
      Object.entries(resolve).forEach(([key, resolved]) => {
        const { emailLink, emailText } = emailTagTransform(
          key,
          resolved,
          domain,
          starsDomain,
          StarsLink
        );
        if (emailText) {
          newValues[key] = emailText;
        }
        if (emailLink) {
          newValues[key] = emailLink;
        }
      });
    }
    return newValues;
  }, [
    appDomain,
    brand,
    brandName,
    domain,
    rawValues,
    resolve,
    starsDomain,
    isSiteDE,
    rewardsBrand
  ]);

  return values;
};
